import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { namedChainsApi, type NamedChainRow } from "./api/named-chains-client";
import { NamedChainCreateModal } from "./NamedChainCreateModal";
import { AdminListHeader } from "../../../components/admin/AdminListHeader";
import "../admin.css";

export function NamedChainListPage() {
  const navigate = useNavigate();
  const [chains, setChains] = useState<NamedChainRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    namedChainsApi
      .list()
      .then((data) => {
        if (!cancelled) setChains(data.chains);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load chains");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lowerSearch = search.trim().toLowerCase();
  const filtered = lowerSearch
    ? chains.filter((c) => c.name.toLowerCase().includes(lowerSearch))
    : chains;

  return (
    <div className="admin-page">
      <AdminListHeader
        title="Named Chains"
        count={chains.length}
        primaryAction={{ label: "+ New chain", onClick: () => setShowCreate(true) }}
      />

      {chains.length > 0 && (
        <div className="jt-header-actions" style={{ padding: "0 16px 8px" }}>
          <input
            className="jt-search"
            type="text"
            placeholder="Search named chains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {isLoading && (
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {!isLoading && !error && chains.length === 0 && (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No named chains yet. Build a reusable chain to share across export mappings.
          </span>
          <button
            type="button"
            className="rb-btn rb-btn-save"
            style={{ marginTop: "var(--space-4)" }}
            onClick={() => setShowCreate(true)}
          >
            + New chain
          </button>
        </div>
      )}

      {!isLoading && !error && chains.length > 0 && (
        <div className="jt-list">
          {filtered.length === 0 && (
            <div className="jt-empty">
              No named chains match &ldquo;{search}&rdquo;.
            </div>
          )}
          {filtered.map((chain) => (
            <div key={chain.id} className="jt-card">
              <div className="jt-card-header">
                <span className="jt-card-name">{chain.name}</span>
              </div>
              {chain.description && (
                <div className="jt-card-desc">{chain.description}</div>
              )}
              <div className="jt-card-meta">
                <span>{chain.usageCount ?? 0} uses</span>
                <span>&middot;</span>
                <span>{chain.steps?.length ?? 0} steps</span>
              </div>
              <div className="jt-card-actions">
                <Link to={`/admin/named-chains/${chain.id}`} className="jt-action-btn">
                  View &rarr;
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <NamedChainCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/admin/named-chains/${id}`);
          }}
        />
      )}
    </div>
  );
}
